import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
  useOutsideClick,
} from "@chakra-ui/react";
import {
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subMonths,
  subYears,
} from "date-fns";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./calanderRangeComponent.css";

const specialOptions = [
  { label: "Last Month", value: "last_month" },
  { label: "Year-to-date", value: "year_to_date" },
  { label: "Last Year", value: "last_year" },
  { label: "Custom", value: "custom" },
];

const getDateRangeForOption = (value, baseDate = new Date()) => {
  switch (value) {
    case "last_month": {
      const date = baseDate;
      return {
        startDate: startOfMonth(date),
        endDate: endOfMonth(date),
      };
    }
    case "year_to_date": {
      return {
        startDate: startOfYear(baseDate),
        endDate: endOfMonth(baseDate),
      };
    }
    case "last_year": {
      const date = subYears(baseDate, 1);
      return {
        startDate: startOfYear(date),
        endDate: endOfYear(date),
      };
    }
    default: {
      if (/^\d{4}-\d{2}$/.test(value)) {
        const [year, month] = value.split("-").map(Number);
        const parsed = new Date(year, month - 1, 1); // month is 0-based
        return {
          startDate: new Date(parsed.getFullYear(), parsed.getMonth(), 1),
          endDate: new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0),
        };
      }
      return { startDate: null, endDate: null };
    }
  }
};

const ReportCalendarComponent = ({
  onClose,
  onSave,
  sDate,
  eDate,
  direction = "right",
}) => {
  const [dateRange, setDateRange] = useState([
    sDate ? sDate : null,
    eDate ? eDate : null,
  ]);
  const ref = useRef(null);

  useOutsideClick({
    ref,
    handler: () => onClose?.(),
  });

  const handleSave = () => {
    const [start, end] = dateRange;
    if (start && end) {
      onSave(start, end);
    }
  };

  return (
    <Box
      position="absolute"
      mt={2}
      zIndex={100}
      ref={ref}
      background="white"
      boxShadow="lg"
      borderRadius="md"
      right={direction === "right" ? 0 : undefined}
      left={direction === "left" ? 0 : undefined}
    >
      <Card>
        <CardBody
          display="flex"
          flexDir="column"
          alignItems="flex-end" // ← horizontal right alignment
        >
          <DatePicker
            selected={dateRange[0]}
            onChange={(update) => setDateRange(update)}
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            selectsRange
            inline
            renderDayContents={(day, date) => {
              const zeroTimeDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
              ).getTime();

              const zeroTimeStart = dateRange[0]
                ? new Date(
                    dateRange[0].getFullYear(),
                    dateRange[0].getMonth(),
                    dateRange[0].getDate(),
                  ).getTime()
                : null;

              const zeroTimeEnd = dateRange[1]
                ? new Date(
                    dateRange[1].getFullYear(),
                    dateRange[1].getMonth(),
                    dateRange[1].getDate(),
                  ).getTime()
                : null;

              const isStart = zeroTimeDate === zeroTimeStart;
              const isEnd = zeroTimeDate === zeroTimeEnd;
              const sameDayStartEnd =
                zeroTimeStart && zeroTimeEnd && zeroTimeStart === zeroTimeEnd;

              const isConfirmedInRange =
                zeroTimeStart &&
                zeroTimeEnd &&
                zeroTimeDate > zeroTimeStart &&
                zeroTimeDate < zeroTimeEnd;

              const isInRange = isConfirmedInRange;

              let fillStyle = {
                position: "absolute",
                top: 0,
                bottom: 0,
                left: -3,
                right: -3,
                backgroundColor: "#E0E6FF",
                borderRadius: 0,
                zIndex: 0,
              };

              if (isStart && !sameDayStartEnd) {
                fillStyle = {
                  ...fillStyle,
                  left: "50%",
                  right: -5,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                };
              } else if (isEnd && !sameDayStartEnd) {
                fillStyle = {
                  ...fillStyle,
                  left: -5,
                  right: "50%",
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                };
              }

              const isHighlightedDay = isStart || isEnd;

              const dayCircleStyle = {
                position: "relative",
                zIndex: 1,
                backgroundColor: isHighlightedDay ? "#536DEC" : "transparent",
                color: isHighlightedDay ? "white" : "black",
                borderRadius: "50%",
                width: 32,
                height: 32,
                lineHeight: "32px",
                textAlign: "center",
                fontWeight: 500,
                fontSize: 14,
                margin: 0,
                userSelect: "none",
              };

              return (
                <div style={{ position: "relative", width: 32, height: 32 }}>
                  {zeroTimeStart &&
                    zeroTimeEnd &&
                    (isInRange ||
                      (isStart && !sameDayStartEnd) ||
                      (isEnd && !sameDayStartEnd)) && <div style={fillStyle} />}
                  <div style={dayCircleStyle}>{day}</div>
                </div>
              );
            }}
          />
          <Button
            onClick={handleSave}
            bgColor={"#536DEC"}
            size="sm"
            mt={2}
            width="81px"
          >
            Save
          </Button>
        </CardBody>
      </Card>
    </Box>
  );
};

const CustomDropdown = ({
  onChange,
  dateFromNav,
  startDate,
  endDate,
  setStart,
  setEnd,
  category,
  setCategory,
  direction = "right",
}) => {
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const buttonRef = useRef(null);

  const MIN_DATE = new Date(2024, 0, 1);

  const getMonthOptions = (baseDate) => {
    const options = [];
    const referenceDate = baseDate || new Date();

    let current = new Date(
      referenceDate.getFullYear(),
      referenceDate.getMonth(),
      1,
    );

    while (current >= MIN_DATE) {
      options.push({
        label: format(current, "MMMM yyyy"),
        value: format(current, "yyyy-MM"),
      });

      current = subMonths(current, 1);
    }

    return options;
  };

  const [monthOptions, setMonthOptions] = useState(getMonthOptions(endDate));

  useEffect(() => {
    setMonthOptions(getMonthOptions(dateFromNav));
  }, [dateFromNav]);

  const findLabel = (val) =>
    [...specialOptions, ...monthOptions].find((o) => o.value === val)?.label;

  const handleSelect = (value) => {
    if (value === "custom") {
      setCategory(value);
      setShowCustomPicker(true);
      return;
    }

    setCategory(value);
    const { startDate, endDate } = getDateRangeForOption(value, dateFromNav);
    setStart?.(startDate);
    setEnd?.(endDate);
    const label = findLabel(value);
    onChange?.({ label, value, startDate, endDate });
  };

  const getStyles = (isSelected) => ({
    bg: isSelected ? "#536DEC" : "white",
    color: isSelected ? "white" : "black",
    fontWeight: "normal",
    borderRadius: "8px",
    _hover: {
      color: isSelected ? "white" : "#536DEC",
      bg: isSelected ? "#536DEC" : "white",
    },
    _focus: { bg: isSelected ? "#536DEC" : "white" },
  });

  return (
    <Box position="relative" display="inline-block">
      <Menu
        isLazy
        placement={direction === "left" ? "bottom-start" : "bottom-end"}
        portal={false}
      >
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          width="auto"
          bg="white"
          color="black"
          border={"1px solid #D1D4DA"}
          _hover={{ bg: "white", border: "1px solid #536DEC" }}
          _expanded={{ bg: "white", border: "1px solid #536DEC" }}
          ref={buttonRef}
        >
          {findLabel(category)}
        </MenuButton>
        <MenuList
          border={"1px solid #D1D4DA"}
          bg="white"
          height="60vh"
          maxH="645px"
          minH="20px"
          overflowY="auto"
          width="210px"
          minW="unset"
          maxW="210px"
          left="50%"
          transform="translateX(-50%)"
          p={2}
          sx={{
            pr: "5px",
            pl: "5px",
            boxSizing: "content-box",

            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#30384A",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "gray.100",
              borderRadius: "4px",
              marginBlock: "4px",
            },
          }}
        >
          <VStack align="start" width="100%" spacing={1}>
            {specialOptions.map((opt) => {
              const isSelected = category === opt.value;
              return (
                <MenuItem
                  key={opt.value}
                  {...getStyles(isSelected)}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </MenuItem>
              );
            })}
            <Divider />
            {monthOptions.map((opt) => {
              const isSelected = category === opt.value;
              return (
                <MenuItem
                  key={opt.value}
                  {...getStyles(isSelected)}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </MenuItem>
              );
            })}
          </VStack>
        </MenuList>
      </Menu>

      {showCustomPicker && (
        <ReportCalendarComponent
          direction={direction}
          sDate={startDate}
          eDate={endDate}
          onClose={() => setShowCustomPicker(false)}
          onSave={(start, end) => {
            setStart?.(start);
            setEnd?.(end);
            onChange?.({
              label: "Custom",
              value: "custom",
              startDate: start,
              endDate: end,
            });
            setShowCustomPicker(false);
          }}
        />
      )}
    </Box>
  );
};

export default CustomDropdown;
